import { z } from 'zod';
import Cookies from 'js-cookie';
import { Filter } from 'contexts/preferences/listing-params';
import { HttpRequestMethodTypes, JSON_CONTENT_TYPE_HEADER } from 'types';
import { FetchOptions } from 'types/fetchWithRetry';
import { fetchWithRetry } from 'utils/fetchWithRetry';
import { getJSON } from 'utils/cookies';
import { USER_COOKIE_NAME } from 'constants/cookies';
import { Meta } from 'utils/types';
import { SavedSearchObservabilityService } from 'observability';
import { SavedSearch, SavedSearchImpl } from './types';
import { ensureBackwardCompatibilityForHomeType } from './utils';
import type { SavedSearchRequestParams, GetSavedSearchResponse } from './api';

// Numeric frequency wire values shared with the legacy client-app API and the UI.
const FREQUENCY_OFF = 0;
const FREQUENCY_INSTANT = -1;
const FREQUENCY_DAILY = 86400;
const FREQUENCY_WEEKLY = 604800;
const FREQUENCY_MONTHLY = 2592000;

const SAVED_SEARCHES_PROXY = '/api/saved-searches';
const EMPTY_META: Meta = { totalPages: 0, totalCount: 0, pageNumber: 0, pageSize: 0 };

// The storage service stores the filter verbatim. Validate it is a JSON object and
// carry the domain Filter type; deep filter validation is intentionally skipped since
// the filter shape is owned by our own writes.
const storageSavedSearchSchema = z.object({
  id: z.string(),
  source: z.string(),
  description: z.string(),
  formParams: z.object({ filter: z.custom<Filter>(val => val !== null && typeof val === 'object') }),
  frequency: z.enum(['instant', 'daily', 'weekly', 'monthly']),
  enabled: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
const storageListSchema = z.object({ savedSearches: z.array(storageSavedSearchSchema) });

type StorageSavedSearch = z.infer<typeof storageSavedSearchSchema>;
type StorageFrequency = StorageSavedSearch['frequency'];

const sessionUserSchema = z.object({
  jwt: z.string().optional(),
  email: z.string().optional(),
  locale: z.string().optional(),
});
type SessionUser = z.infer<typeof sessionUserSchema>;

function getSessionUser(): SessionUser | null {
  const raw = getJSON(Cookies.get(USER_COOKIE_NAME));
  if (!raw) {
    return null;
  }
  const parsed = sessionUserSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

function authHeaders(jwt: string): FetchOptions['headers'] {
  return { ...JSON_CONTENT_TYPE_HEADER, Authorization: `Bearer ${jwt}` };
}

// Map the UI's numeric frequency to the storage service's { frequency, enabled }.
function numberToStorageFrequency(frequency: number): { frequency: StorageFrequency; enabled: boolean } {
  if (frequency === FREQUENCY_INSTANT) {
    return { frequency: 'instant', enabled: true };
  }
  if (frequency <= FREQUENCY_OFF) {
    return { frequency: 'daily', enabled: false };
  }
  if (frequency === FREQUENCY_WEEKLY) {
    return { frequency: 'weekly', enabled: true };
  }
  if (frequency === FREQUENCY_MONTHLY) {
    return { frequency: 'monthly', enabled: true };
  }
  // 86400 (daily) and any other positive value default to daily-enabled,
  // matching the legacy client-app behaviour (e.g. the quick "save with alerts" path).
  return { frequency: 'daily', enabled: true };
}

// Inverse mapping for responses.
function storageFrequencyToNumber(frequency: StorageFrequency, enabled: boolean): number {
  if (!enabled) {
    return FREQUENCY_OFF;
  }
  switch (frequency) {
    case 'instant':
      return FREQUENCY_INSTANT;
    case 'weekly':
      return FREQUENCY_WEEKLY;
    case 'monthly':
      return FREQUENCY_MONTHLY;
    case 'daily':
    default:
      return FREQUENCY_DAILY;
  }
}

function toSavedSearch(item: StorageSavedSearch): SavedSearchImpl {
  return SavedSearchImpl.fromStorage({
    id: item.id,
    source: item.source,
    description: item.description,
    frequency: storageFrequencyToNumber(item.frequency, item.enabled),
    formParams: item.formParams,
    imageUrl: null,
    listingsCount: null,
    newListingsCount: null,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  });
}

// Keep deprecated homeType shapes backward-compatible before sending to storage.
function normalizeFilter(search: Partial<SavedSearch>): void {
  const homeType = search.formParams?.filter?.homeType;
  if (search.formParams && homeType) {
    search.formParams.filter.homeType = ensureBackwardCompatibilityForHomeType(homeType);
  }
}

/**
 * Saved-search data access backed by the DynamoDB storage service (reached via
 * the /api/saved-searches Next.js proxy, which validates and forwards the
 * session JWT). Exposes the same static surface as SavedSearchApiManager so the
 * data-layer wrapper can switch between them behind a feature flag.
 */
export class SavedSearchStorageApiManager {
  private constructor() {}

  static async get(params: SavedSearchRequestParams): Promise<GetSavedSearchResponse> {
    const user = getSessionUser();
    if (!user?.jwt) {
      return { savedSearches: [], meta: EMPTY_META };
    }

    try {
      const query = params.source ? `?source=${encodeURIComponent(params.source)}` : '';
      const response = await fetchWithRetry(`${SAVED_SEARCHES_PROXY}${query}`, {
        method: HttpRequestMethodTypes.GET,
        headers: authHeaders(user.jwt),
      });
      if (!response.ok) {
        return { savedSearches: [], meta: EMPTY_META };
      }

      const parsed = storageListSchema.safeParse(await response.json());
      if (!parsed.success) {
        console.error('failed to parse saved-search list from storage service', parsed.error);
        return { savedSearches: [], meta: EMPTY_META };
      }

      // Newest first, matching the legacy API's created_at desc ordering.
      const all = parsed.data.savedSearches
        .map(toSavedSearch)
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      // The storage list endpoint returns the full set; paginate client-side to
      // preserve the wrapper's existing pagination contract.
      const size = params.page.size;
      const pageNumber = params.page.number ?? 1;
      const start = (pageNumber - 1) * size;
      const savedSearches = size > 0 ? all.slice(start, start + size) : all;
      const meta: Meta = {
        totalCount: all.length,
        pageSize: size,
        pageNumber,
        totalPages: size > 0 ? Math.ceil(all.length / size) : 1,
      };

      return { savedSearches, meta };
    } catch {
      return { savedSearches: [], meta: EMPTY_META };
    }
  }

  static async create(search: Partial<SavedSearch>): Promise<SavedSearch> {
    const user = getSessionUser();
    if (!user?.jwt) {
      const error = { errors: [{ attribute: 'auth', message: 'Not authenticated' }]};
      SavedSearchObservabilityService.trackCreateFailure({ error, errorData: { filterData: search.formParams?.filter }});
      throw error;
    }

    normalizeFilter(search);
    const { frequency, enabled } = numberToStorageFrequency(search.frequency ?? FREQUENCY_OFF);
    const body = {
      source: search.source,
      email: user.email,
      locale: user.locale,
      description: search.description,
      formParams: search.formParams,
      frequency,
      enabled,
    };

    const response = await fetchWithRetry(SAVED_SEARCHES_PROXY, {
      method: HttpRequestMethodTypes.POST,
      headers: authHeaders(user.jwt),
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const error = { errors: [{ attribute: 'base', message: 'Your search was not saved.' }]};
      SavedSearchObservabilityService.trackCreateFailure({ error, errorData: { filterData: search.formParams?.filter }});
      throw error;
    }

    const parsed = storageSavedSearchSchema.safeParse(await response.json());
    if (!parsed.success) {
      SavedSearchObservabilityService.trackCreateFailure({ error: parsed.error, errorData: { filterData: search.formParams?.filter }});
      throw parsed.error;
    }
    return toSavedSearch(parsed.data);
  }

  static async update(search: Partial<SavedSearch>): Promise<void> {
    if (!search.id) {
      throw new Error('Saved search ID is required to update a saved search.');
    }

    const user = getSessionUser();
    if (!user?.jwt) {
      const error = new Error('Not authenticated');
      SavedSearchObservabilityService.trackUpdateFailure({ error, errorData: { savedSearchId: search.id, filterData: search.formParams?.filter }});
      throw error;
    }

    normalizeFilter(search);
    const body: Record<string, unknown> = { email: user.email, locale: user.locale };
    if (search.description !== undefined) {
      body.description = search.description;
    }
    if (search.formParams !== undefined) {
      body.formParams = search.formParams;
    }
    if (search.frequency !== undefined) {
      const { frequency, enabled } = numberToStorageFrequency(search.frequency);
      body.frequency = frequency;
      body.enabled = enabled;
    }

    const response = await fetchWithRetry(`${SAVED_SEARCHES_PROXY}/${search.id}`, {
      method: HttpRequestMethodTypes.PATCH,
      headers: authHeaders(user.jwt),
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const error = new Error(`Failed to update saved search: ${response.status}`);
      SavedSearchObservabilityService.trackUpdateFailure({ error, errorData: { savedSearchId: search.id, filterData: search.formParams?.filter }});
      console.error('failed to update saved search ', error);
      throw error;
    }
  }

  static async delete(id: string): Promise<void> {
    const user = getSessionUser();
    if (!user?.jwt) {
      const error = new Error('Not authenticated');
      SavedSearchObservabilityService.trackDeleteFailure({ error, errorData: { savedSearchId: id }});
      throw error;
    }

    const response = await fetchWithRetry(`${SAVED_SEARCHES_PROXY}/${id}`, {
      method: HttpRequestMethodTypes.DELETE,
      headers: authHeaders(user.jwt),
    });
    if (!response.ok) {
      const error = new Error(`Failed to delete saved search: ${response.status}`);
      SavedSearchObservabilityService.trackDeleteFailure({ error, errorData: { savedSearchId: id }});
      console.error('failed to delete saved search', error);
      throw error;
    }
  }
}
